import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  ViewChildren,
  QueryList,
  ChangeDetectorRef,
  inject,
  effect,
} from "@angular/core";
import {
  FormulareClientProvider,
  FormularDetailDtoIncludes,
} from "src/app/web-api/web-api";
import { FormBuilder, FormGroup } from "@angular/forms";
import {
  FormularModel,
  FormularModelValidator,
  assertFormularModel,
} from "./formular-model/FormularModel";
import { FormularGroup } from "./formular-model/formular-groups/interfaces/FormularGroup";
import { CommonKeys, FormularModelKeys } from "./formular-model/common/enums";
import { FormularGroupTabComponent } from "./components/formular-group-tab/group-tab.components";
import { HttpClient } from "@angular/common/http";
import { CstResponsivePopupService } from "src/app/common-components/resp-popup/services/resp-popup.service";

import { BerichtImageUploadProgressPopupComponent } from "./components/formular-group-tab/berichte/upload-progress-popup/upload-progress-popup";
import { ImageToUpload } from "./components/formular-group-tab/berichte/berichte.component";
import { firstValueFrom, Observable } from "rxjs";
import { AlertService } from "src/app/services";
import { FORMULAR_GROUP_SIGNATUREN_TYPE } from "./formular-model/common/formular-group-types";
import { FormularInitTracker } from "./services/init-tracker/formular-init-tracker.service";

// add group types to this list to skip them when initializing the form
const IGNORE_GROUP_LIST = [FORMULAR_GROUP_SIGNATUREN_TYPE];

@Component({
  selector: "app-formular-editor",
  templateUrl: "./task-formular-edit.component.html",
  styleUrls: ["./task-formular-edit.component.scss"],
  providers: [FormularInitTracker],
  standalone: false,
})
export class TaskFormularEditComponent implements OnInit {
  private _formularClient = inject(FormulareClientProvider);
  private _fb = inject(FormBuilder);
  private _validator = inject(FormularModelValidator);
  private _snackBar = inject(AlertService);
  private _http = inject(HttpClient);
  private _popup = inject(CstResponsivePopupService);
  private _cdr = inject(ChangeDetectorRef);
  private _tracker = inject(FormularInitTracker);

  @Input() set editedFormularId(value: string) {
    this._formularId = value;
    this.isFormularLoading = true;
  }

  @ViewChildren(FormularGroupTabComponent)
  formularGroupTabs!: QueryList<FormularGroupTabComponent>;

  private _formularId!: string;
  private _formularRaw!: object;
  private _formularObject!: FormularModel;
  private _formularForm: FormGroup;

  public tabsArray: any[] = [];
  public isFormularLoading = false;
  public isDataReady = false;
  private _isClosing = false;

  public get formular(): FormularModel {
    return this._formularObject;
  }
  public get formularForm() {
    return this._formularForm;
  }
  public get formularGroups() {
    return this._formularObject[FormularModelKeys.Groups][CommonKeys.Values];
  }

  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() close = new EventEmitter<null>();

  constructor() {
    this._formularForm = this._fb.group({});

    effect(() => {
      if (this._isClosing) return;

      const errors = this._tracker.errors();
      if (errors.length > 0) {
        this._isClosing = true;
        console.error("Tab init failed:", errors);
        const message = errors
          .map(([tabKey, err]) => {
            const title =
              this.tabsArray.find((t) => t.formKey === tabKey)?.title ?? tabKey;
            return `${title}: ${err.message}`;
          })
          .join("; ");
        this._close(message || "An error has occurred, quitting.");
        return;
      }

      if (this._tracker.allReady() && this.isFormularLoading) {
        this.isFormularLoading = false;
        this._cdr.detectChanges();
      }
    });
  }

  async ngOnInit() {
    const result = await this.initializeFormular();

    if (!result.success) {
      console.error("Formular initialization failed:", result.errors);
      this._close(`Could not load form: ${result.errors.join(", ")}`);
      return;
    }

    this.isDataReady = true;
    this._cdr.detectChanges();
  }

  private async initializeFormular() {
    const result = {
      success: true,
      errors: [] as string[],
    };

    try {
      const data = await firstValueFrom(
        this._formularClient.getSingle(
          this._formularId,
          undefined,
          FormularDetailDtoIncludes.JsonDocument,
        ),
      );

      this._formularRaw = data.jsonDocument;

      if (!this._validator.validate(this._formularRaw)) {
        result.success = false;
        result.errors.push("Data did not pass validation.");
        return result;
      }

      this._formularObject = assertFormularModel(this._formularRaw);

      this.tabsArray = this.formularGroups
        .map((groupData, i) => {
          if (IGNORE_GROUP_LIST.includes(groupData[CommonKeys.Type])) return;
          const groupForm = this._fb.group({});
          const formKey = this.getUniqueGroupKey(groupData, i);
          this._formularForm.addControl(formKey, groupForm);
          return {
            formKey,
            title: groupData[CommonKeys.Label],
            data: groupData,
            ctl: groupForm,
          };
        })
        .filter((g) => g !== undefined);
    } catch (error) {
      result.success = false;
      result.errors.push(
        `Initialization error: ${error instanceof Error ? error.message : String(error)}`,
      );
    }

    return result;
  }

  trackByFn(_: number, item: any) {
    return item.formKey || item.id;
  }

  public async reconstructFormular() {
    const reconstructionResults = {
      success: true,
      errors: [] as string[],
      formularObject: null as FormularModel | null,
      uploads: [] as ImageToUpload[],
    };

    const trackerErrors = this._tracker.errors();
    if (trackerErrors.length > 0) {
      reconstructionResults.success = false;
      reconstructionResults.errors.push(
        ...trackerErrors.map(([key, _]) => `Tab ${key} has unresolved errors`),
      );
      return reconstructionResults;
    }

    try {
      const reconstructedGroups: any[] = [];
      const tabs = this.formularGroupTabs.toArray();

      for (let i = 0; i < tabs.length; i++) {
        const tabComponent = tabs[i];
        try {
          const reconstructedData = await tabComponent.getTabData();
          if ("imagesToUpload" in reconstructedData) {
            reconstructionResults.uploads.push(
              ...reconstructedData.imagesToUpload,
            );
            reconstructedGroups.push(reconstructedData.res);
          } else {
            reconstructedGroups.push(reconstructedData);
          }
        } catch (error) {
          reconstructionResults.success = false;
          reconstructionResults.errors.push(
            `Tab ${i} (${this.tabsArray[i]?.title}): ${
              error instanceof Error ? error.message : String(error)
            }`,
          );
        }
      }

      if (reconstructionResults.success) {
        this._formularObject[FormularModelKeys.Groups][CommonKeys.Values] =
          reconstructedGroups;
        reconstructionResults.formularObject = this._formularObject;
      }
    } catch (error) {
      reconstructionResults.success = false;
      reconstructionResults.errors.push(
        `General reconstruction error: ${
          error instanceof Error ? error.message : String(error)
        }`,
      );
    }

    return reconstructionResults;
  }

  public onFormularEditorClose() {
    this.close.emit(null);
  }

  public async onFormularEditorAccept() {
    if (!this.formularGroupTabs || this.formularGroupTabs.length === 0) {
      console.warn("No child components found");
      return;
    }

    const reconstructedFormular = await this.reconstructFormular();

    if (reconstructedFormular.success) {
      const isValid = this._validator.validate(
        reconstructedFormular.formularObject,
      );
      if (!isValid) {
        console.error("Reconstructed formular failed validation");
        return;
      }

      const result = await this.saveFormularToServer(
        reconstructedFormular.formularObject,
      );

      if (result) {
        if (reconstructedFormular.uploads.length > 0) {
          this._uploadImages(reconstructedFormular.uploads).subscribe({
            error: (err) => console.error(err),
          });
        }
        this._close();
      } else {
        throw new Error(
          `Encountered an error while uploading Formular ${reconstructedFormular.formularObject.FormularID}`,
        );
      }
    } else if (reconstructedFormular.errors) {
      console.error(
        "Encountered errors while reconstructing the form:",
        reconstructedFormular.errors,
      );
      this._snack(
        "Encountered errors while reconstructing the form (more info in console)",
      );
    }
  }

  private _uploadImages(uploads: ImageToUpload[]): Observable<any> {
    const dialogRef = this._popup.open<
      BerichtImageUploadProgressPopupComponent,
      {
        uploads: ImageToUpload[];
        formularID: string;
      },
      boolean | null
    >(BerichtImageUploadProgressPopupComponent, {
      data: {
        uploads: uploads,
        formularID: this._formularObject[CommonKeys.ID],
      },
    });

    return dialogRef.afterClosed();
  }

  private _snack(message: string) {
    this._snackBar.showSnackbar(message, {
      actionText: "OK",
      duration: "short",
    });
  }

  private _close(message?: string) {
    if (message && message !== "") {
      this._snack(message);
    }

    this.close.emit();
  }

  public getTabComponent(index: number): FormularGroupTabComponent | undefined {
    return this.formularGroupTabs?.toArray()[index];
  }

  private async saveFormularToServer(
    formularData: FormularModel,
  ): Promise<unknown> {
    console.log("Saving formular to server:", formularData);

    const payload = {
      formularID: formularData.FormularID,
      jsonDocument: JSON.stringify(formularData),
    };

    try {
      const res = await firstValueFrom(
        this._http.post("/api/Formular/Formulare/ModelUpdate", payload),
      );
      return res;
    } catch (err) {
      console.error("Save failed:", err);
      return null;
    }
  }

  public hasWartungsStates() {
    return this.formular[FormularModelKeys.Meta].WartungsStates !== null;
  }

  public withMetadata(obj: object) {
    return {
      ...obj,
      FormularID: this.formular[FormularModelKeys.FID],
      WartungsStates:
        this.formular[FormularModelKeys.Meta]["WartungsStates"][
          CommonKeys.Values
        ],
      MaterialActionNames:
        this.formular[FormularModelKeys.Meta]["MaterialActionNames"][
          CommonKeys.Values
        ],
    };
  }

  getUniqueGroupKey(groupData: FormularGroup, index: number) {
    return groupData["ID"] || groupData["Label"] + index;
  }
}
